/**
 * oemConfig 全局管理工具
 * 提供获取、设置、监听 oemConfig 的功能
 */

/**
 * 获取 oemConfig
 * @returns {Object|null} oemConfig 对象或 null
 */
export function getOemConfig() {
  try {
    const config = localStorage.getItem('oemConfig');
    return config ? JSON.parse(config) : null;
  } catch (error) {
    console.error('获取 oemConfig 失败:', error);
    return null;
  }
}

/**
 * 设置 oemConfig
 * @param {Object} config - 要设置的配置对象
 * @returns {boolean} 是否设置成功
 */
export function setOemConfig(config) {
  try {
    localStorage.setItem('oemConfig', JSON.stringify(config));
    // 触发自定义事件，通知其他模块 oemConfig 已更新
    window.dispatchEvent(new CustomEvent('oemConfigChanged', { 
      detail: { config } 
    }));
    return true;
  } catch (error) {
    console.error('设置 oemConfig 失败:', error);
    return false;
  }
}

/**
 * 监听 oemConfig 变化
 * @param {Function} callback - 回调函数，参数为新的 oemConfig
 * @returns {Function} 返回取消监听的函数
 */
export function watchOemConfig(callback) {
  const handleChange = (event) => {
    if (event.type === 'storage' && event.key === 'oemConfig') {
      try {
        const newConfig = event.newValue ? JSON.parse(event.newValue) : null;
        callback(newConfig);
      } catch (error) {
        console.error('解析 oemConfig 失败:', error);
        callback(null);
      }
    } else if (event.type === 'oemConfigChanged') {
      callback(event.detail.config);
    }
  };

  // 监听 localStorage 变化（跨标签页）
  window.addEventListener('storage', handleChange);
  
  // 监听自定义事件（同标签页内）
  window.addEventListener('oemConfigChanged', handleChange);

  // 返回取消监听的函数
  return () => {
    window.removeEventListener('storage', handleChange);
    window.removeEventListener('oemConfigChanged', handleChange);
  };
}

/**
 * 获取 oemConfig 的特定属性
 * @param {string} key - 属性键名
 * @param {*} defaultValue - 默认值
 * @returns {*} 属性值或默认值
 */
export function getOemConfigValue(key, defaultValue = null) {
  const config = getOemConfig();
  if (!config) return defaultValue;
  
  // 支持嵌套属性，如 'theme.color'
  return key.split('.').reduce((obj, k) => {
    return obj && obj[k] !== undefined ? obj[k] : defaultValue;
  }, config);
}

/**
 * 检查 oemConfig 是否存在
 * @returns {boolean} 是否存在
 */
export function hasOemConfig() {
  return getOemConfig() !== null;
}

/**
 * 清除 oemConfig
 * @returns {boolean} 是否清除成功
 */
export function clearOemConfig() {
  try {
    localStorage.removeItem('oemConfig');
    // 触发清除事件
    window.dispatchEvent(new CustomEvent('oemConfigChanged', { 
      detail: { config: null } 
    }));
    return true;
  } catch (error) {
    console.error('清除 oemConfig 失败:', error);
    return false;
  }
}

/**
 * 动态更换浏览器图标
 * @param {string} iconUrl - 图标URL
 * @param {string} iconType - 图标类型，可选值：'icon', 'apple-touch-icon', 'shortcut'
 */
export function changeFavicon(iconUrl, iconType = 'icon') {
  try {
    // 移除现有的图标
    const existingIcons = document.querySelectorAll(`link[rel="${iconType}"]`);
    existingIcons.forEach(icon => icon.remove());
    
    // 创建新的图标链接
    const newIcon = document.createElement('link');
    newIcon.rel = iconType;
    newIcon.href = iconUrl;
    
    // 添加到 head 中
    document.head.appendChild(newIcon);
    
    console.log(`图标已更换为: ${iconUrl}`);
    return true;
  } catch (error) {
    console.error('更换图标失败:', error);
    return false;
  }
}

/**
 * 批量更换所有类型的图标
 * @param {Object} iconConfig - 图标配置对象
 * @param {string} iconConfig.icon - 标准图标URL
 * @param {string} iconConfig.appleTouchIcon - Apple触摸图标URL
 * @param {string} iconConfig.shortcutIcon - 快捷方式图标URL
 */


/**
 * 从 oemConfig 中获取图标配置并应用
 * @param {Object} config - oemConfig 对象
 */
export function applyFaviconFromConfig(config) {
  if (!config) return false;
  return changeFavicon(config.ossPath + 'icon.png', 'icon');
  
}

export default {
  getOemConfig,
  setOemConfig,
  watchOemConfig,
  getOemConfigValue,
  hasOemConfig,
  clearOemConfig,
  changeFavicon,
  applyFaviconFromConfig
};

// 导出 Vue 原型方法，用于在 main.js 中注册
export const oemConfigMethods = {
  getOemConfig,
  setOemConfig,
  watchOemConfig,
  smartGetOemConfig,
  safeClearLocalStorage,
  clearLocalStorageExceptOemConfig,
  clearLocalStorageExcluding
};

/**
 * 智能获取 oemConfig - 解决第一次获取不到数据的问题
 * @param {number} timeout - 超时时间（毫秒），默认 3000ms
 * @param {number} interval - 检查间隔（毫秒），默认 100ms
 * @returns {Promise<Object|null>} 返回配置对象或 null
 */
export function smartGetOemConfig(timeout = 3000, interval = 100) {
  return new Promise((resolve) => {
    // 先尝试直接获取
    let config = getOemConfig();
    if (config) {
      resolve(config);
      return;
    }

    // 如果没有获取到，开始轮询等待
    const startTime = Date.now();
    
    const checkConfig = () => {
      config = getOemConfig();
      
      if (config) {
        console.log('智能获取 oemConfig 成功:', config);
        resolve(config);
        return;
      }
      
      // 检查是否超时
      if (Date.now() - startTime > timeout) {
        console.warn('智能获取 oemConfig 超时，返回 null');
        resolve(null);
        return;
      }
      
      // 继续等待
      setTimeout(checkConfig, interval);
    };
    
    console.log('开始智能等待 oemConfig...');
    checkConfig();
  });
}

/**
 * 同步等待 oemConfig（阻塞式，不推荐在组件中使用）
 * @param {number} timeout - 超时时间（毫秒），默认 3000ms
 * @returns {Object|null} 返回配置对象或 null
 */
export function waitForOemConfigSync(timeout = 3000) {
  const startTime = Date.now();
  
  while (Date.now() - startTime < timeout) {
    const config = getOemConfig();
    if (config) {
      return config;
    }
    
    // 阻塞等待 50ms
    const waitStart = Date.now();
    while (Date.now() - waitStart < 50) {
      // 空循环等待
    }
  }
  
  return null;
}

/**
 * 安全清除 localStorage，保留 oemConfig 数据
 * @param {Array} preserveKeys - 需要保留的键名数组，默认包含 'oemConfig'
 * @returns {boolean} 是否清除成功
 */
export function safeClearLocalStorage(preserveKeys = ['oemConfig']) {
  try {
    // 保存需要保留的数据
    const preservedData = {};
    preserveKeys.forEach(key => {
      const value = localStorage.getItem(key);
      if (value !== null) {
        preservedData[key] = value;
      }
    });
    
    // 清除所有 localStorage
    localStorage.clear();
    
    // 恢复需要保留的数据
    Object.entries(preservedData).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
    
    console.log('安全清除 localStorage 成功，保留了以下数据:', preserveKeys);
    return true;
  } catch (error) {
    console.error('安全清除 localStorage 失败:', error);
    return false;
  }
}

/**
 * 清除除 oemConfig 外的所有 localStorage 数据
 * @returns {boolean} 是否清除成功
 */
export function clearLocalStorageExceptOemConfig() {
  return safeClearLocalStorage(['oemConfig']);
}

/**
 * 清除除指定键外的所有 localStorage 数据
 * @param {Array} excludeKeys - 需要排除的键名数组
 * @returns {boolean} 是否清除成功
 */
export function clearLocalStorageExcluding(excludeKeys) {
  return safeClearLocalStorage(excludeKeys);
} 